import { CheckedState } from '@headless-tree/core'
import type { TreeItem } from '@/api/content/connector'

export type ConnectorTreeSelectionNode = Readonly<{
  data: TreeItem
  parentId: string | null
}>

export type ConnectorTreeSelectionModel = Readonly<{
  nodes: ReadonlyMap<string, ConnectorTreeSelectionNode>
  explicitCheckedItemIds: ReadonlySet<string>
  lazyCheckedFolderIds: ReadonlySet<string>
  explicitUncheckedItemIds: ReadonlySet<string>
  demotedFolderIds: ReadonlySet<string>
  seededIndeterminateItemIds: ReadonlySet<string>
}>

export function createConnectorTreeSelectionModel(): ConnectorTreeSelectionModel {
  return {
    nodes: new Map(),
    explicitCheckedItemIds: new Set(),
    lazyCheckedFolderIds: new Set(),
    explicitUncheckedItemIds: new Set(),
    demotedFolderIds: new Set(),
    seededIndeterminateItemIds: new Set()
  }
}

function cloneTreeItem(item: TreeItem): TreeItem {
  return { ...item, children: [...item.children] }
}

export function loadConnectorTreeSelectionItem(
  model: ConnectorTreeSelectionModel,
  item: TreeItem,
  parentId: string | null
): ConnectorTreeSelectionModel {
  const nodes = new Map(model.nodes)
  const existing = nodes.get(item.id)
  nodes.set(item.id, { data: cloneTreeItem(item), parentId })
  if (existing) return { ...model, nodes }

  const explicitCheckedItemIds = new Set(model.explicitCheckedItemIds)
  const lazyCheckedFolderIds = new Set(model.lazyCheckedFolderIds)
  const seededIndeterminateItemIds = new Set(model.seededIndeterminateItemIds)

  if (item.checked === CheckedState.Checked) {
    explicitCheckedItemIds.add(item.id)
    if (item.isFolder) lazyCheckedFolderIds.add(item.id)
  } else if (item.checked === CheckedState.Indeterminate) {
    seededIndeterminateItemIds.add(item.id)
  }

  return {
    ...model,
    nodes,
    explicitCheckedItemIds,
    lazyCheckedFolderIds,
    seededIndeterminateItemIds
  }
}

export function loadConnectorTreeSelectionChildren(
  model: ConnectorTreeSelectionModel,
  parentId: string,
  children: ReadonlyArray<TreeItem>
): ConnectorTreeSelectionModel {
  return children.reduce((nextModel, child) => loadConnectorTreeSelectionItem(nextModel, child, parentId), model)
}

function loadedDescendantIds(model: ConnectorTreeSelectionModel, itemId: string): Array<string> {
  const descendants: Array<string> = []
  const visited = new Set<string>()

  function collect(parentId: string) {
    if (visited.has(parentId)) return
    visited.add(parentId)
    const parent = model.nodes.get(parentId)
    if (!parent) return
    for (const childId of parent.data.children) {
      if (!model.nodes.has(childId)) continue
      descendants.push(childId)
      collect(childId)
    }
  }

  collect(itemId)
  return descendants
}

function inheritsChecked(model: ConnectorTreeSelectionModel, itemId: string): boolean {
  let currentId: string | null = itemId
  const visited = new Set<string>()
  while (currentId) {
    if (visited.has(currentId)) return false
    visited.add(currentId)
    if (model.explicitUncheckedItemIds.has(currentId)) return false
    if (currentId !== itemId && model.lazyCheckedFolderIds.has(currentId)) return true
    currentId = model.nodes.get(currentId)?.parentId ?? null
  }
  return false
}

function getConnectorTreeSelectionStateInternal(
  model: ConnectorTreeSelectionModel,
  itemId: string,
  resolving: Set<string>
): CheckedState {
  if (model.demotedFolderIds.has(itemId)) return CheckedState.Indeterminate
  if (model.explicitUncheckedItemIds.has(itemId)) return CheckedState.Unchecked
  if (model.explicitCheckedItemIds.has(itemId)) return CheckedState.Checked
  if (inheritsChecked(model, itemId)) return CheckedState.Checked
  if (model.seededIndeterminateItemIds.has(itemId)) return CheckedState.Indeterminate

  const node = model.nodes.get(itemId)
  if (!node || resolving.has(itemId)) return CheckedState.Unchecked
  resolving.add(itemId)
  const hasSelectedChild = node.data.children.some(
    (childId) =>
      model.nodes.has(childId) &&
      getConnectorTreeSelectionStateInternal(model, childId, resolving) !== CheckedState.Unchecked
  )
  resolving.delete(itemId)
  return hasSelectedChild ? CheckedState.Indeterminate : CheckedState.Unchecked
}

export function getConnectorTreeSelectionState(model: ConnectorTreeSelectionModel, itemId: string): CheckedState {
  return getConnectorTreeSelectionStateInternal(model, itemId, new Set())
}

type MutableSelectionSets = {
  explicitCheckedItemIds: Set<string>
  lazyCheckedFolderIds: Set<string>
  explicitUncheckedItemIds: Set<string>
  demotedFolderIds: Set<string>
  seededIndeterminateItemIds: Set<string>
}

function mutableSets(model: ConnectorTreeSelectionModel): MutableSelectionSets {
  return {
    explicitCheckedItemIds: new Set(model.explicitCheckedItemIds),
    lazyCheckedFolderIds: new Set(model.lazyCheckedFolderIds),
    explicitUncheckedItemIds: new Set(model.explicitUncheckedItemIds),
    demotedFolderIds: new Set(model.demotedFolderIds),
    seededIndeterminateItemIds: new Set(model.seededIndeterminateItemIds)
  }
}

function withSets(model: ConnectorTreeSelectionModel, sets: MutableSelectionSets): ConnectorTreeSelectionModel {
  return { ...model, ...sets }
}

function normalizeAncestors(
  model: ConnectorTreeSelectionModel,
  itemId: string,
  sets: MutableSelectionSets
): ConnectorTreeSelectionModel {
  let nextModel = withSets(model, sets)
  let ancestorId = model.nodes.get(itemId)?.parentId ?? null
  const visited = new Set<string>()

  while (ancestorId) {
    if (visited.has(ancestorId)) break
    visited.add(ancestorId)
    const ancestor = model.nodes.get(ancestorId)
    if (!ancestor) break

    const hasSelectedChild = ancestor.data.children.some(
      (childId) =>
        model.nodes.has(childId) && getConnectorTreeSelectionState(nextModel, childId) !== CheckedState.Unchecked
    )
    if (hasSelectedChild) {
      sets.demotedFolderIds.add(ancestorId)
      sets.seededIndeterminateItemIds.delete(ancestorId)
    } else {
      sets.explicitCheckedItemIds.delete(ancestorId)
      sets.lazyCheckedFolderIds.delete(ancestorId)
      sets.demotedFolderIds.delete(ancestorId)
      sets.seededIndeterminateItemIds.delete(ancestorId)
    }
    nextModel = withSets(model, sets)
    ancestorId = ancestor.parentId
  }

  return nextModel
}

export function checkConnectorTreeSelectionItem(
  model: ConnectorTreeSelectionModel,
  itemId: string
): ConnectorTreeSelectionModel {
  const node = model.nodes.get(itemId)
  if (!node) throw new Error(`Cannot check unloaded tree item: ${itemId}`)

  const sets = mutableSets(model)
  sets.explicitCheckedItemIds.add(itemId)
  sets.explicitUncheckedItemIds.delete(itemId)
  sets.demotedFolderIds.delete(itemId)
  sets.seededIndeterminateItemIds.delete(itemId)
  if (node.data.isFolder) sets.lazyCheckedFolderIds.add(itemId)

  if (node.data.isFolder) {
    for (const descendantId of loadedDescendantIds(model, itemId)) {
      sets.explicitUncheckedItemIds.delete(descendantId)
      sets.demotedFolderIds.delete(descendantId)
      sets.seededIndeterminateItemIds.delete(descendantId)
    }
  }

  return normalizeAncestors(model, itemId, sets)
}

export function uncheckConnectorTreeSelectionItem(
  model: ConnectorTreeSelectionModel,
  itemId: string
): ConnectorTreeSelectionModel {
  const node = model.nodes.get(itemId)
  if (!node) throw new Error(`Cannot uncheck unloaded tree item: ${itemId}`)

  const sets = mutableSets(model)
  sets.explicitCheckedItemIds.delete(itemId)
  sets.lazyCheckedFolderIds.delete(itemId)
  sets.explicitUncheckedItemIds.add(itemId)
  sets.demotedFolderIds.delete(itemId)
  sets.seededIndeterminateItemIds.delete(itemId)

  if (node.data.isFolder) {
    for (const descendantId of loadedDescendantIds(model, itemId)) {
      sets.explicitCheckedItemIds.delete(descendantId)
      sets.lazyCheckedFolderIds.delete(descendantId)
      sets.demotedFolderIds.delete(descendantId)
      sets.seededIndeterminateItemIds.delete(descendantId)
    }
  }

  return normalizeAncestors(model, itemId, sets)
}

export function serializeConnectorTreeSelection(
  model: ConnectorTreeSelectionModel,
  rootItemId: string
): Record<string, TreeItem> {
  const serialized: Record<string, TreeItem> = {}
  const visited = new Set<string>()

  function serialize(itemId: string, includeUncheckedRoot = false) {
    if (visited.has(itemId)) return
    visited.add(itemId)
    const node = model.nodes.get(itemId)
    if (!node) return
    const checked = getConnectorTreeSelectionState(model, itemId)
    if (!includeUncheckedRoot && checked === CheckedState.Unchecked) return
    serialized[itemId] = { ...cloneTreeItem(node.data), checked }
    for (const childId of node.data.children) serialize(childId)
  }

  serialize(rootItemId, true)
  return serialized
}
